import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';

/**
 * A client's online accounts (P-56; Arthur, A-#506, A-#512): the "Client accounts" panel on the Dashboard. Plain text,
 * versioned, read by everyone who sees the client; changed by client_master at write (`may_write`).
 *
 * The values are asked for only by the open panel's own child (OpenAccounts, ClientAccountsPanel.tsx), so they are read
 * only while it is open, and dropped from the query cache the moment it closes: closing unmounts the child, the query
 * has no observer left, and `gcTime: 0` starts then (Code 1's read, F1: an observer kept by `enabled: false` never lets
 * it start). Never kept for half an hour as the other queries are, never written to storage, and never in an address
 * (every value travels in a POST body).
 */
export type AccountKind = 'email' | 'login' | 'phone';

export interface AccountValues {
  label: string | null;
  login: string | null;
  password: string | null;
  phone_number: string | null;
  carrier: string | null;
  pin: string | null;
}

export interface ClientAccount extends AccountValues {
  id: string;
  kind: AccountKind;
  version: number;
  created_at: string;
  ended_at: string | null;
  ended_reason: string | null;
}

export interface AccountVersion extends AccountValues {
  version: number;
  edit_reason: string | null;
  edit_reason_label: string | null;
  reason_text: string | null;
  recorded_at: string;
  recorded_by: string | null;
}

const VALUES_ONLY_WHILE_SHOWN = { gcTime: 0 } as const;

/** Called ONLY by OpenAccounts, which exists only while the panel is open: no `enabled`, and nothing else may call it. */
export function useClientAccounts(clientId: string) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'online-accounts'],
    queryFn: () => apiFetch<{ accounts: ClientAccount[]; may_write: boolean }>(`/clients/${clientId}/accounts`, getToken),
    ...queryDefaults,
    ...VALUES_ONLY_WHILE_SHOWN,
  });
}

export function useAccountVersions(clientId: string, accountId: string, open: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'online-accounts', accountId, 'versions'],
    queryFn: () => apiFetch<{ versions: AccountVersion[] }>(`/client-accounts/${accountId}/versions`, getToken),
    ...queryDefaults,
    ...VALUES_ONLY_WHILE_SHOWN,
    enabled: open,
  });
}

function useInvalidateAccounts(clientId: string) {
  const queryClient = useQueryClient();
  return () => { void queryClient.invalidateQueries({ queryKey: ['client', clientId, 'online-accounts'] }); };
}

const post = (body: unknown) => ({
  method: 'POST',
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body),
});

export function useRecordAccount(clientId: string) {
  const { getToken } = useSession();
  const invalidate = useInvalidateAccounts(clientId);
  return useMutation({
    mutationFn: (input: { kind: AccountKind; values: Partial<AccountValues> }) =>
      apiFetch<{ id: string }>(`/clients/${clientId}/accounts`, getToken, post(input)),
    onSuccess: invalidate,
  });
}

export function useEditAccount(clientId: string) {
  const { getToken } = useSession();
  const invalidate = useInvalidateAccounts(clientId);
  return useMutation({
    mutationFn: (input: { id: string; values: Partial<AccountValues>; reason: string; reason_text?: string }) =>
      apiFetch<{ version: number }>(`/client-accounts/${input.id}/edit`, getToken,
        post({ values: input.values, reason: input.reason, ...(input.reason_text ? { reason_text: input.reason_text } : {}) })),
    onSuccess: invalidate,
  });
}

export function useEndAccount(clientId: string) {
  const { getToken } = useSession();
  const invalidate = useInvalidateAccounts(clientId);
  return useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      apiFetch<{ ended: boolean }>(`/client-accounts/${input.id}/end`, getToken, post({ reason: input.reason })),
    onSuccess: invalidate,
  });
}
